import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Sparkles, Info, ShoppingCart, Store } from "lucide-react";
import { api } from "../lib/api";
import { formatEUR, countryFlag } from "../lib/format";
import { useAuth } from "../context/AuthContext";

// Dealer-visible 8-step timeline color mapping.
const STEP_STYLES = {
  richiesta_ricevuta: { pill: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
  verifica_in_corso:  { pill: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500" },
  trattativa:         { pill: "bg-indigo-50 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  accordo_raggiunto:  { pill: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500" },
  documenti:          { pill: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  pagamento:          { pill: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  trasporto_consegna: { pill: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-600" },
  conclusa:           { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  annullata:          { pill: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
};

const TYPE_KEYS = {
  acquisto: "acquisto",
  offerta_completa: "offerta_completa",
  solo_verifica: "solo_verifica",
  domanda: "domanda",
};

export default function PratichePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("acquisti");

  useEffect(() => {
    api.get("/pratiche").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  if (items === null) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const buyerItems = items.filter((p) => p.viewer_role === "buyer");
  const sellerItems = items.filter((p) => p.viewer_role === "seller");
  const canSell = !!user?.dealer_id;
  const active = tab === "vendite" ? sellerItems : buyerItems;

  return (
    <div className="py-6 md:py-10" data-testid="pratiche-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {t("pratiche.overline")}
        </p>
        <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
          {t("pratiche.title")}
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">{t("pratiche.subtitle")}</p>
      </div>

      {/* Tabs */}
      {canSell && (
        <div
          data-testid="pratiche-tabs"
          className="mt-6 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1"
        >
          <button
            type="button"
            data-testid="pratiche-tab-acquisti"
            onClick={() => setTab("acquisti")}
            className={`text-xs font-semibold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
              tab === "acquisti" ? "bg-white text-slate-950 shadow" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <ShoppingCart className="w-3.5 h-3.5" /> {t("pratiche.tab_acquisti")}
            <span className="rounded-full bg-slate-200 text-slate-700 px-1.5 tabular-nums text-[10px]">
              {buyerItems.length}
            </span>
          </button>
          <button
            type="button"
            data-testid="pratiche-tab-vendite"
            onClick={() => setTab("vendite")}
            className={`text-xs font-semibold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
              tab === "vendite" ? "bg-white text-slate-950 shadow" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Store className="w-3.5 h-3.5" /> {t("pratiche.tab_vendite")}
            <span className="rounded-full bg-slate-200 text-slate-700 px-1.5 tabular-nums text-[10px]">
              {sellerItems.length}
            </span>
          </button>
        </div>
      )}

      {active.length === 0 ? (
        <div className="mt-8 rounded-2xl bg-white border border-slate-100 p-10 text-center" data-testid="pratiche-empty">
          <Info className="w-6 h-6 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            {tab === "vendite" ? t("pratiche.empty_vendite") : t("pratiche.empty")}
          </p>
          {tab !== "vendite" && (
            <p className="mt-1 text-xs text-slate-400">{t("pratiche.empty_hint")}</p>
          )}
          <Link
            to={tab === "vendite" ? "/le-mie-auto" : "/marketplace"}
            data-testid="pratiche-cta-marketplace"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600"
          >
            {tab === "vendite"
              ? t("pratiche.manage_stock_cta")
              : t("pratiche.empty_go_marketplace")}{" "}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <ul className="mt-8 space-y-3" data-testid="pratiche-list">
          {active.map((p, i) => (
            <PraticaRow key={p.id} pratica={p} viewerRole={p.viewer_role} delay={i * 0.03} />
          ))}
        </ul>
      )}

      {/* Service strip */}
      <ServiceStrip />
    </div>
  );
}

function PraticaRow({ pratica, viewerRole = "buyer", delay = 0 }) {
  const { t } = useTranslation();
  const style = STEP_STYLES[pratica.dealer_status] || STEP_STYLES.richiesta_ricevuta;
  const v = pratica.vehicle;
  const price = pratica.negotiated_price || v?.price_eur || pratica.requested_price;
  const isSeller = viewerRole === "seller";

  return (
    <motion.li
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
    >
      <Link
        to={`/pratiche/${pratica.id}`}
        data-testid={`pratica-row-${pratica.id}`}
        className="flex items-center gap-4 bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-shadow"
      >
        <div className="w-20 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">
          {v?.images?.[0] && (
            <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-semibold text-sm tracking-tight truncate">
              {v ? `${v.brand} ${v.model}` : t("pratiche.title")}
            </div>
            <span
              className={`text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1 rounded-md px-2 py-0.5 border ${style.pill}`}
              data-testid={`pratica-status-${pratica.id}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
              {t(`pratiche.step.${pratica.dealer_status}`)}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-slate-500 truncate">
            {pratica.code} · {t(`pratiche.type.${TYPE_KEYS[pratica.type] || "acquisto"}`)} ·{" "}
            {v?.year || ""}{" "}
            {v?.country ? `· ${countryFlag(v.country)} ${v.country}` : ""}
            {isSeller && pratica.buyer_dealer?.name && (
              <span className="ml-1">· {t("pratiche.buyer_prefix")} {pratica.buyer_dealer.name}</span>
            )}
          </div>
        </div>
        {price != null && (
          <div className="text-right shrink-0">
            <div className="font-heading font-bold text-sm tabular-nums text-slate-950">
              {formatEUR(price)}
            </div>
            {pratica.negotiated_price && (
              <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
                {t("pratiche.negotiated")}
              </div>
            )}
          </div>
        )}
      </Link>
    </motion.li>
  );
}

function ServiceStrip() {
  const { t } = useTranslation();
  const items = [
    "trattativa",
    "verifica",
    "documenti",
    "pagamenti",
    "trasporto",
    "consegna",
  ];
  return (
    <div className="mt-10 rounded-2xl bg-gradient-to-br from-slate-950 to-slate-900 text-white p-6 md:p-8 relative overflow-hidden">
      <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-blue-600/20 blur-3xl" />
      <div className="relative z-10">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-300">
          <Sparkles className="w-3 h-3" /> {t("pratiche.service_strip_overline")}
        </div>
        <h3 className="mt-2 font-heading font-bold text-xl tracking-tight max-w-lg">
          {t("pratiche.service_strip_title")}
        </h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {items.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-100 bg-white/10 border border-white/20 rounded-full px-2.5 py-1"
            >
              {t(`pratiche.service_item.${k}`)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
